import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
    StringOrNumberAttribute,
} from '../../../attributes';
import type { CanvasTextDynamicBoxRendererSettings } from '../renderer/CanvasTextDynamicBoxRenderer';
import { CanvasTextDynamicBoxRenderer } from '../renderer/CanvasTextDynamicBoxRenderer';
import { Config } from '../../../../video-data/config';
import type { DynamicHeight, DynamicWidth } from '../../../../enums';
import {
    DynamicInferredHeight,
    DynamicInferredWidth,
    DynamicLevel,
    DynamicXDirection,
    DynamicYDirection,
} from '../../../../enums';
import { int2rgba } from '../../../../libs/videoData';
import type { LoadSettings } from '../../../serializableMixin';
import { cloneDeep } from '../../../../libs/cloneDeep';
import type {
    BoxGraphicAnimationXDirection,
    BoxGraphicAnimationYDirection,
} from '../../../slide/graphicsStyle/BoxGraphic';
import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

/**
 * TextAssetDynamicBox is the Luminary equivalent of CanvasTextDynamicBox and provides a bridge between Luminary
 * and the CanvasText library.
 */
export class TextAssetDynamicBox extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicBox';

    override readonly type = Config.BOX_GRAPHIC;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredWidth) })
    accessor boxWidth: DynamicWidth = DynamicInferredWidth.width;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredHeight) })
    accessor boxHeight: DynamicHeight = DynamicInferredHeight.maxHeight;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor topLeftRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor topRightRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor bottomLeftRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor bottomRightRadius = 0;
    @NumberAttribute()
    accessor paddingTop = 0;
    @NumberAttribute()
    accessor paddingRight = 0;
    @NumberAttribute()
    accessor paddingBottom = 0;
    @NumberAttribute()
    accessor paddingLeft = 0;
    @StringAttribute({ choices: Object.values(DynamicXDirection) })
    accessor xDirection = DynamicXDirection.right;
    @StringAttribute({ choices: Object.values(DynamicYDirection) })
    accessor yDirection = DynamicYDirection.bottom;
    @NumberAttribute()
    accessor xProgress = 1.0;
    @NumberAttribute()
    accessor yProgress = 1.0;
    @ColorAttribute()
    accessor strokeColor = 0xffffff;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fill = 0xff00ff;
    @AlphaAttribute()
    accessor fillAlpha = 0.5;

    override getCanvasRendererSettings(): CanvasTextDynamicBoxRendererSettings {
        const base = super.getCanvasRendererSettings();

        const boxWidth =
            typeof this.boxWidth === 'number'
                ? this.boxWidth * this.referenceToOutputMultiplier
                : this.boxWidth;
        const boxHeight =
            typeof this.boxHeight === 'number'
                ? this.boxHeight * this.referenceToOutputMultiplier
                : this.boxHeight;
        const strokeStyle = int2rgba(this.strokeColor, this.strokeAlpha);

        return {
            ...base,
            type: this.type,
            boxWidth,
            boxHeight,
            topLeftRadius: this.topLeftRadius * this.referenceToOutputMultiplier,
            topRightRadius: this.topRightRadius * this.referenceToOutputMultiplier,
            bottomLeftRadius: this.bottomLeftRadius * this.referenceToOutputMultiplier,
            bottomRightRadius: this.bottomRightRadius * this.referenceToOutputMultiplier,
            paddingTop: this.paddingTop * this.referenceToOutputMultiplier,
            paddingRight: this.paddingRight * this.referenceToOutputMultiplier,
            paddingBottom: this.paddingBottom * this.referenceToOutputMultiplier,
            paddingLeft: this.paddingLeft * this.referenceToOutputMultiplier,
            strokeStyle,
            strokeWidth: this.strokeWidth * this.referenceToOutputMultiplier,
            xDirection: this.xDirection,
            yDirection: this.yDirection,
            xProgress: this.xProgress,
            yProgress: this.yProgress,
            fill: int2rgba(this.fill, this.fillAlpha),
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since CanvasTextDynamicBox is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return CanvasTextDynamicBoxRenderer.animatableProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat(['fill', 'fillAlpha', 'strokeColor', 'strokeAlpha']);
    }

    override get animatableGeometricProperties() {
        return CanvasTextDynamicBoxRenderer.animatableGeometricProperties;
    }

    override loadTextAssetStyle(data, settings: LoadSettings) {
        const newData = cloneDeep(data);
        // Our old box graphics had two sets of properties that did the same thing. Scale & Progress animations
        // had the same effect, but the canvas renderer only supports Progress. So we need to remap scale.x
        // to xProgress (and the same for y).
        newData.animation.forEach((animation) => {
            if (animation.property === 'scale.x') {
                animation.property = 'xProgress';
            }
            if (animation.property === 'scale.y') {
                animation.property = 'yProgress';
            }
        });

        newData.boxWidth = data.shape.boxWidth;
        let boxHeight: DynamicHeight = data.shape.boxHeight;
        // In the old text, gapless height only worked for line level, otherwise it used max height
        if (this.level !== DynamicLevel.line && boxHeight === DynamicInferredHeight.gapless) {
            boxHeight = DynamicInferredHeight.maxHeight;
        }
        if (boxHeight === Config.INFERRED_HEIGHT_WITHOUT_DESCENT) {
            boxHeight = DynamicInferredHeight.heightWithoutDescent;
        }
        newData.boxHeight = boxHeight;
        newData.topLeftRadius = data.shape.topLeftRadius;
        newData.topRightRadius = data.shape.topRightRadius;
        newData.bottomLeftRadius = data.shape.bottomLeftRadius;
        newData.bottomRightRadius = data.shape.bottomRightRadius;
        newData.paddingTop = data.shape.paddingTop;
        newData.paddingRight = data.shape.paddingRight;
        newData.paddingBottom = data.shape.paddingBottom;
        newData.paddingLeft = data.shape.paddingLeft;
        if (data.shape.strokeAlpha !== undefined) {
            newData.strokeAlpha = data.shape.strokeAlpha;
        }
        if (data.shape.strokeColor !== undefined) {
            newData.strokeColor = data.shape.strokeColor;
        }
        if (data.shape.strokeWidth !== undefined) {
            newData.strokeWidth = data.shape.strokeWidth;
        }
        if (data.shape.fillColor !== undefined) {
            newData.fill = data.shape.fillColor;
        }
        if (data.shape.fillAlpha !== undefined) {
            newData.fillAlpha = data.shape.fillAlpha;
        }
        newData.xDirection = this.#migrateTextAssetStyleXDirection(data.shape.horizontalDirection);
        newData.yDirection = this.#migrateTextAssetStyleYDirection(data.shape.verticalDirection);
        newData.xProgress = data.shape.progressXValue;
        newData.yProgress = data.shape.progressYValue;

        super.loadTextAssetStyle(newData, settings);
    }

    #migrateTextAssetStyleXDirection(direction: BoxGraphicAnimationXDirection): DynamicXDirection {
        if (direction === Config.ZIG_ZAG_LEFT_RIGHT_DIRECTION) {
            return DynamicXDirection.zigZagLeft;
        }
        if (direction === Config.ZIG_ZAG_RIGHT_LEFT_DIRECTION) {
            return DynamicXDirection.zigZagRight;
        }

        if (direction === Config.LEFT_ANIMATION_DIRECTION) {
            return DynamicXDirection.left;
        }

        if (direction === Config.RIGHT_ANIMATION_DIRECTION) {
            return DynamicXDirection.right;
        }

        if (direction === Config.CENTER_ANIMATION_DIRECTION) {
            return DynamicXDirection.center;
        }
    }

    #migrateTextAssetStyleYDirection(direction: BoxGraphicAnimationYDirection): DynamicYDirection {
        if (direction === Config.ZIG_ZAG_BOTTOM_TOP_DIRECTION) {
            return DynamicYDirection.bottom;
        }
        if (direction === Config.ZIG_ZAG_TOP_BOTTOM_DIRECTION) {
            return DynamicYDirection.bottom;
        }

        if (direction === Config.TOP_ANIMATION_DIRECTION) {
            return DynamicYDirection.top;
        }

        if (direction === Config.BOTTOM_ANIMATION_DIRECTION) {
            return DynamicYDirection.bottom;
        }

        if (direction === Config.CENTER_ANIMATION_DIRECTION) {
            return DynamicYDirection.center;
        }
    }
}
